import { HttpErrorResponse } from '@angular/common/http';
import type {
  HttpErrorCode,
  ResolveHttpErrorContext,
  ResolvedHttpError,
} from '@indutrust/shared-model';

const ANGULAR_HTTP_FAILURE_PREFIX = 'Http failure response for ';

/** Maps normalized API message text to stable store / i18n codes. */
const KNOWN_API_MESSAGE_CODES: Readonly<Record<string, HttpErrorCode>> = {
  'invalid credentials': 'invalid_credentials',
};

function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}

function isErrorBodyRecord(body: unknown): body is Record<string, unknown> {
  return body !== null && typeof body === 'object';
}

function extractApiMessage(error: HttpErrorResponse): string | undefined {
  const body: unknown = error.error;

  if (typeof body === 'string' && body.trim().length > 0) {
    return body.trim();
  }

  if (isErrorBodyRecord(body)) {
    const errorField = body['error'];

    if (typeof errorField === 'string' && errorField.trim().length > 0) {
      return errorField.trim();
    }

    const messageField = body['message'];

    if (typeof messageField === 'string' && messageField.trim().length > 0) {
      return messageField.trim();
    }
  }

  return undefined;
}

function normalizeMessageKey(message: string): string {
  return message.trim().toLowerCase();
}

function messageToCode(message: string): HttpErrorCode {
  const normalized = normalizeMessageKey(message);
  let code: HttpErrorCode = 'unknown';

  if (normalized in KNOWN_API_MESSAGE_CODES) {
    code = KNOWN_API_MESSAGE_CODES[normalized];
  } else {
    const trimmed = message.trim();

    if (/^[a-z][a-z0-9_-]*$/.test(trimmed)) {
      code = trimmed;
    }
  }

  return code;
}

function statusToCode(status: number): HttpErrorCode {
  if (status === 0) {
    return 'network';
  }

  if (status === 401) {
    return 'unauthorized';
  }

  if (status === 403) {
    return 'forbidden';
  }

  if (status === 404) {
    return 'not_found';
  }

  if (status === 409) {
    return 'conflict';
  }

  if (status === 422) {
    return 'validation';
  }

  if (status >= 500) {
    return 'server';
  }

  return 'unknown';
}

function resolveFromHttpError(
  error: HttpErrorResponse,
  context?: ResolveHttpErrorContext
): ResolvedHttpError {
  const apiMessage = extractApiMessage(error);
  const statusCode = statusToCode(error.status);

  if (context?.loginAttempt && error.status === 401) {
    return {
      code: 'invalid_credentials',
      debugMessage: apiMessage ?? error.message,
    };
  }

  if (apiMessage !== undefined) {
    return {
      code: messageToCode(apiMessage),
      debugMessage: apiMessage,
    };
  }

  return {
    code: statusCode,
    debugMessage: error.message,
  };
}

/**
 * Normalizes an HTTP or unknown failure into a stable {@link HttpErrorCode}
 * for store state. Never returns Angular's default `HttpErrorResponse.message`
 * as the code (it embeds the request URL).
 */
export function resolveHttpError(
  error: unknown,
  context?: ResolveHttpErrorContext
): ResolvedHttpError {
  if (isHttpErrorResponse(error)) {
    return resolveFromHttpError(error, context);
  }

  if (error instanceof Error) {
    const message = error.message.trim();

    if (message.startsWith(ANGULAR_HTTP_FAILURE_PREFIX)) {
      return { code: 'unknown', debugMessage: message };
    }

    if (message.length > 0) {
      return { code: messageToCode(message), debugMessage: message };
    }
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    const trimmed = error.trim();

    if (trimmed.startsWith(ANGULAR_HTTP_FAILURE_PREFIX)) {
      return { code: 'unknown', debugMessage: trimmed };
    }

    return { code: messageToCode(trimmed), debugMessage: trimmed };
  }

  return { code: 'unknown' };
}
